import {
	createGoogleSheetIntegration,
	getGoogleSheetConnectInfo,
	verifyGoogleSheetIntegration,
} from '@pwa/queries/google-sheets/google-sheets';
import { Button } from '@repo/ui/components/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@repo/ui/components/card';
import { Input } from '@repo/ui/components/input';
import { Spinner } from '@repo/ui/components/spinner';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
	ArrowLeft,
	ArrowRight,
	CheckCircle2,
	ClipboardCopy,
	ExternalLink,
	FileSpreadsheet,
	Link2,
	Mail,
	ShieldCheck,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_dashboard/org/$orgId/integrations/google-sheets/create')({
	component: RouteComponent,
});

type Step = 'connect' | 'create' | 'verify';

function RouteComponent() {
	const { orgId } = Route.useParams();
	const navigate = useNavigate();

	const [step, setStep] = useState<Step>('connect');
	const [connectInfo, setConnectInfo] = useState<Awaited<ReturnType<typeof getGoogleSheetConnectInfo>> | null>(null);
	const [connectInfoLoading, setConnectInfoLoading] = useState(false);
	const [connectInfoError, setConnectInfoError] = useState<string | null>(null);
	const [url, setUrl] = useState('');
	const [urlError, setUrlError] = useState<string | null>(null);
	const [createResult, setCreateResult] = useState<Awaited<ReturnType<typeof createGoogleSheetIntegration>> | null>(
		null,
	);
	const [codeCopied, setCodeCopied] = useState(false);

	// ---------------------------------------------------------------------------
	// Load connect info (service account email) on mount
	// ---------------------------------------------------------------------------

	useEffect(() => {
		setConnectInfoLoading(true);
		setConnectInfoError(null);
		getGoogleSheetConnectInfo(orgId)
			.then(setConnectInfo)
			.catch((err: Error) => setConnectInfoError(err.message ?? 'Failed to load connect info'))
			.finally(() => setConnectInfoLoading(false));
	}, [orgId]);

	// ---------------------------------------------------------------------------
	// Mutations
	// ---------------------------------------------------------------------------

	const createMutation = useMutation({
		mutationFn: (sheetUrl: string) => createGoogleSheetIntegration(orgId, sheetUrl),
		onSuccess: (result) => {
			setCreateResult(result);
			setStep('verify');
		},
		onError: (err: Error) => {
			toast.error(err.message);
		},
	});

	const verifyMutation = useMutation({
		mutationFn: (integrationId: string) => verifyGoogleSheetIntegration(orgId, integrationId),
		onSuccess: (result) => {
			toast.success(result.message ?? 'Integration verified successfully');
			const verified = result.integration;
			if (verified) {
				void navigate({
					to: '/org/$orgId/integrations/google-sheets/$integrationId',
					params: { orgId, integrationId: verified.id },
				});
			} else if (createResult) {
				// Fallback: navigate using original integration ID from create step
				void navigate({
					to: '/org/$orgId/integrations/google-sheets/$integrationId',
					params: { orgId, integrationId: createResult.integration.id },
				});
			}
		},
		onError: (err: Error) => {
			toast.error(err.message);
		},
	});

	const handleVerify = useCallback(() => {
		if (!createResult) return;
		verifyMutation.mutate(createResult.integration.id);
	}, [createResult, verifyMutation]);

	const handleSubmitUrl = useCallback(
		(e: React.FormEvent) => {
			e.preventDefault();
			setUrlError(null);

			const trimmed = url.trim();
			if (!trimmed) {
				setUrlError('Please enter a Google Sheets URL');
				return;
			}

			const isValid = /^https:\/\/(?:docs\.)?google\.com\/spreadsheets\/d\/[a-zA-Z0-9_-]+/u.test(trimmed);
			if (!isValid) {
				setUrlError('Enter a valid Google Sheets URL (e.g. https://docs.google.com/spreadsheets/d/{id}/edit)');
				return;
			}

			createMutation.mutate(trimmed);
		},
		[url, createMutation],
	);

	const handleCopyCode = useCallback(async () => {
		if (!createResult) return;
		try {
			await navigator.clipboard.writeText(createResult.verificationCode);
			setCodeCopied(true);
			toast.success('Verification code copied');
		} catch {
			toast.error('Failed to copy. Select and copy the code manually.');
		}
	}, [createResult]);

	return (
		<div className='container mx-auto flex max-h-[calc(100vh-4rem)] max-w-3xl flex-col gap-6 overflow-y-auto p-6 pb-12'>
			<Link
				to='/org/$orgId/integrations/google-sheets'
				params={{ orgId }}
				className='group inline-flex w-fit items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground'
			>
				<ArrowLeft
					className='size-3 transition-transform duration-200 group-hover:-translate-x-0.5'
					aria-hidden='true'
				/>
				Back to linked sheets
			</Link>

			<StepIndicator current={step} />

			{step === 'connect' ? (
				connectInfoLoading ? (
					<Card>
						<CardContent className='flex items-center justify-center p-8'>
							<Spinner className='size-6' aria-label='Loading...' />
						</CardContent>
					</Card>
				) : connectInfoError ? (
					<Card>
						<CardContent className='p-6 text-center'>
							<p className='text-sm text-destructive'>{connectInfoError}</p>
							<Button
								variant='outline'
								size='sm'
								className='mt-4'
								onClick={() => {
									setConnectInfo(null);
									setConnectInfoError(null);
								}}
							>
								Try again
							</Button>
						</CardContent>
					</Card>
				) : connectInfo ? (
					<ConnectStep serviceAccountEmail={connectInfo.serviceAccountEmail} onContinue={() => setStep('create')} />
				) : null
			) : null}

			{step === 'create' && !createResult ? (
				<UrlStep
					url={url}
					urlError={urlError}
					onUrlChange={setUrl}
					onClearError={() => setUrlError(null)}
					onSubmit={handleSubmitUrl}
					isPending={createMutation.isPending}
				/>
			) : null}

			{step === 'verify' && createResult ? (
				<VerifyStep
					result={createResult}
					onCopyCode={handleCopyCode}
					codeCopied={codeCopied}
					onVerify={handleVerify}
					isVerifying={verifyMutation.isPending}
				/>
			) : null}

			{step === 'verify' && !createResult ? (
				<Card>
					<CardContent className='flex items-center justify-center p-8'>
						<Spinner className='size-6' aria-label='Loading...' />
					</CardContent>
				</Card>
			) : null}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Step indicator
// ---------------------------------------------------------------------------

function StepIndicator({ current }: { current: Step }) {
	const steps: { key: Step; label: string }[] = [
		{ key: 'connect', label: 'Share sheet' },
		{ key: 'create', label: 'Link sheet' },
		{ key: 'verify', label: 'Verify' },
	];

	const getIndex = (step: Step) => steps.findIndex((s) => s.key === step);

	return (
		<nav className='flex items-center gap-0' aria-label='Setup progress'>
			{steps.map((s, i) => {
				const currentIdx = getIndex(current);
				const isComplete = i < currentIdx;
				const isCurrent = i === currentIdx;
				return (
					<div key={s.key} className='flex items-center gap-0'>
						<div
							className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
								isCurrent
									? 'bg-emerald-500/12 text-emerald-700 dark:text-emerald-300'
									: isComplete
										? 'text-muted-foreground'
										: 'text-muted-foreground/50'
							}`}
						>
							{isComplete ? (
								<CheckCircle2 className='size-3.5 text-emerald-600' aria-hidden='true' />
							) : (
								<span className='flex size-3.5 items-center justify-center rounded-full border text-[10px] font-bold leading-none'>
									{i + 1}
								</span>
							)}
							<span className={isCurrent ? '' : 'hidden sm:inline'}>{s.label}</span>
						</div>
						{i < steps.length - 1 ? (
							<div
								className={`mx-1 h-px w-6 sm:w-10 ${i < currentIdx ? 'bg-emerald-500/40' : 'bg-border'}`}
								aria-hidden='true'
							/>
						) : null}
					</div>
				);
			})}
		</nav>
	);
}

// ---------------------------------------------------------------------------
// Step 1: Share sheet with Scout (new)
// ---------------------------------------------------------------------------

function ConnectStep({ serviceAccountEmail, onContinue }: { serviceAccountEmail: string; onContinue: () => void }) {
	return (
		<div className='space-y-6'>
			<Card className='border-emerald-500/20 bg-linear-to-br from-emerald-500/[0.04] to-background'>
				<CardHeader>
					<div className='mb-2 flex size-11 items-center justify-center rounded-2xl bg-emerald-500/12 text-emerald-600 ring-1 ring-emerald-500/20'>
						<Mail className='size-5' aria-hidden='true' />
					</div>
					<CardTitle>Share your sheet with Scout</CardTitle>
					<CardDescription>
						Grant Scout API access to your spreadsheet by sharing it with the service account below. You'll still need
						to prove write-access in the next step.
					</CardDescription>
				</CardHeader>
				<CardContent className='space-y-5'>
					<div className='space-y-1.5'>
						<p className='text-xs font-medium text-muted-foreground'>Scout service account email</p>
						<div className='rounded-lg border bg-background px-3 py-2 font-mono text-sm break-all select-all'>
							{serviceAccountEmail}
						</div>
					</div>

					<div className='space-y-2 rounded-lg border p-4'>
						<p className='text-xs font-medium text-muted-foreground'>How it works</p>
						<ol className='space-y-2 text-sm'>
							<li className='flex gap-2'>
								<span className='flex size-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/10 text-xs font-medium text-emerald-600'>
									1
								</span>
								<span>
									Open your Google Sheet and click <strong>Share</strong> in the top-right corner.
								</span>
							</li>
							<li className='flex gap-2'>
								<span className='flex size-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/10 text-xs font-medium text-emerald-600'>
									2
								</span>
								<span>
									Add the email above as an <strong>Editor</strong>.
								</span>
							</li>
							<li className='flex gap-2'>
								<span className='flex size-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/10 text-xs font-medium text-emerald-600'>
									3
								</span>
								<span>
									Come back here and click <strong>Next</strong> to link the sheet.
								</span>
							</li>
						</ol>
					</div>

					<Button onClick={onContinue} className='w-full gap-2'>
						<ArrowRight className='size-4' aria-hidden='true' />
						I've shared the sheet — Next
					</Button>
				</CardContent>
			</Card>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Step 2: URL input (modified — before create)
// ---------------------------------------------------------------------------

function UrlStep({
	url,
	urlError,
	onUrlChange,
	onClearError,
	onSubmit,
	isPending,
}: {
	url: string;
	urlError: string | null;
	onUrlChange: (v: string) => void;
	onClearError: () => void;
	onSubmit: (e: React.FormEvent) => void;
	isPending: boolean;
}) {
	return (
		<Card>
			<CardHeader>
				<div className='mb-2 flex size-11 items-center justify-center rounded-2xl bg-emerald-500/12 text-emerald-600 ring-1 ring-emerald-500/20'>
					<Link2 className='size-5' aria-hidden='true' />
				</div>
				<CardTitle>Link a Google Sheet</CardTitle>
				<CardDescription>Paste the URL of the spreadsheet you shared with Scout in the previous step.</CardDescription>
			</CardHeader>
			<CardContent>
				<form onSubmit={onSubmit} className='space-y-4'>
					<div className='space-y-2'>
						<label htmlFor='sheet-url' className='text-sm font-medium'>
							Spreadsheet URL
						</label>
						<Input
							id='sheet-url'
							type='url'
							placeholder='https://docs.google.com/spreadsheets/d/...'
							value={url}
							onChange={(e) => {
								onUrlChange(e.target.value);
								onClearError();
							}}
							aria-invalid={urlError ? true : undefined}
							aria-describedby={urlError ? 'url-error' : undefined}
							disabled={isPending}
							autoFocus
						/>
						{urlError ? (
							<p id='url-error' className='text-xs text-destructive'>
								{urlError}
							</p>
						) : null}
					</div>
					<div className='rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 text-xs text-muted-foreground'>
						<p className='font-medium text-amber-700 dark:text-amber-400'>What happens next?</p>
						<ul className='mt-1 list-inside list-disc space-y-0.5'>
							<li>Scout validates it can access the sheet (must be shared from the previous step).</li>
							<li>A one-time verification code is generated and written to ScoutX!A1.</li>
							<li>You copy the code into ScoutX!A2 to prove write access.</li>
						</ul>
					</div>
					<Button type='submit' disabled={isPending || !url.trim()} className='gap-2'>
						{isPending ? (
							<>
								<Spinner className='size-4' aria-hidden='true' />
								Validating...
							</>
						) : (
							<>
								<ArrowRight className='size-4' aria-hidden='true' />
								Continue
							</>
						)}
					</Button>
				</form>
			</CardContent>
		</Card>
	);
}

// ---------------------------------------------------------------------------
// Step 3: Verification code + instructions + verify button (combined)
// ---------------------------------------------------------------------------

function VerifyStep({
	result,
	onCopyCode,
	codeCopied,
	onVerify,
	isVerifying,
}: {
	result: Awaited<ReturnType<typeof createGoogleSheetIntegration>>;
	onCopyCode: () => void;
	codeCopied: boolean;
	onVerify: () => void;
	isVerifying: boolean;
}) {
	return (
		<div className='space-y-6'>
			<Card className='border-emerald-500/20 bg-linear-to-br from-emerald-500/[0.04] to-background'>
				<CardHeader>
					<div className='mb-2 flex size-11 items-center justify-center rounded-2xl bg-emerald-500/12 text-emerald-600 ring-1 ring-emerald-500/20'>
						<ShieldCheck className='size-5' aria-hidden='true' />
					</div>
					<CardTitle>Proof-of-control verification</CardTitle>
					<CardDescription>
						Paste the verification code into cell <strong>ScoutX!A2</strong> to prove you have write access to the
						spreadsheet, then verify below.
					</CardDescription>
				</CardHeader>
				<CardContent className='space-y-5'>
					<div className='space-y-1.5'>
						<p className='text-xs font-medium text-muted-foreground'>Verification code</p>
						<div className='flex items-center gap-2'>
							<div className='flex-1 rounded-lg border bg-background px-3 py-2 font-mono text-sm tracking-wider select-all'>
								{result.verificationCode}
							</div>
							<Button variant='outline' size='icon' onClick={onCopyCode} aria-label='Copy verification code'>
								<ClipboardCopy className='size-4' aria-hidden='true' />
							</Button>
						</div>
						{codeCopied ? <p className='text-xs text-emerald-600'>Copied!</p> : null}
					</div>

					<div className='space-y-2 rounded-lg border p-4'>
						<p className='text-xs font-medium text-muted-foreground'>Setup steps</p>
						<ol className='space-y-2 text-sm'>
							<li className='flex gap-2'>
								<span className='flex size-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/10 text-xs font-medium text-emerald-600'>
									1
								</span>
								<span>
									Open your spreadsheet and find (or create) a tab named&nbsp;
									<strong>"{result.verificationInstructions.tab}"</strong>. Paste the verification code into cell&nbsp;
									<strong>
										{result.verificationInstructions.tab}!{result.verificationInstructions.codeCell}
									</strong>
									.
								</span>
							</li>
							<li className='flex gap-2'>
								<span className='flex size-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/10 text-xs font-medium text-emerald-600'>
									2
								</span>
								<span>
									Click <strong>Verify now</strong> below. Scout reads the code from&nbsp;
									{result.verificationInstructions.tab}!{result.verificationInstructions.codeCell} to confirm write
									access.
								</span>
							</li>
							<li className='flex gap-2'>
								<span className='flex size-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/10 text-xs font-medium text-emerald-600'>
									3
								</span>
								<span>
									(Optional) Add context below the verification code (A4+): fill the <strong>Northstar</strong> and
									<strong>Notes</strong> sections so Scout understands what the sheet is for.
								</span>
							</li>
						</ol>
					</div>

					<Button onClick={onVerify} disabled={isVerifying} className='w-full gap-2'>
						{isVerifying ? (
							<>
								<Spinner className='size-4' aria-hidden='true' />
								Verifying...
							</>
						) : (
							<>
								<ShieldCheck className='size-4' aria-hidden='true' />
								Verify now
							</>
						)}
					</Button>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<div className='flex items-center gap-2 text-sm font-medium'>
						<FileSpreadsheet className='size-4 text-emerald-600' aria-hidden='true' />
						<span>Target sheet</span>
					</div>
				</CardHeader>
				<CardContent className='space-y-2 text-sm'>
					<div className='flex justify-between'>
						<span className='text-muted-foreground'>Title</span>
						<span className='font-medium'>{result.integration.spreadsheetTitle ?? 'Unknown'}</span>
					</div>
					<div className='flex justify-between'>
						<span className='text-muted-foreground'>URL</span>
						<a
							href={result.integration.spreadsheetUrl ?? '#'}
							target='_blank'
							rel='noopener noreferrer'
							className='inline-flex items-center gap-1 text-emerald-600 hover:underline'
						>
							Open
							<ExternalLink className='size-3' aria-hidden='true' />
						</a>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
